import { useState, useEffect } from 'react';
import {
    Grid,
    Card,
    CardContent,
    Typography,
    IconButton,
    Box,
    Chip,
    Button,
    Collapse,
    List,
    ListItem,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Switch,
    FormControlLabel,
    Tooltip,
    Paper,
} from '@mui/material';
import {
    CheckCircle as HealthyIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    Refresh as RefreshIcon,
    RestartAlt as RestartIcon,
    History as HistoryIcon,
    Speed as SpeedIcon,
    Timeline as TimelineIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { PieChart } from '@mui/x-charts/PieChart';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer } from 'recharts';
import MDTypography from '../../../../components/MDTypography';
import MDBox from '../../../../components/MDBox';

function SystemHealth() {
    const [engines, setEngines] = useState([
        {
            id: 1,
            name: 'Ingestion Engine',
            status: 'healthy',
            lastCheck: new Date().toISOString(),
            description: 'Handles data ingestion and preprocessing',
            metrics: {
                latency: 45,
                usage: 60,
                errorRate: 2,
                throughput: 850,
            },
            errors: [],
            history: [
                { timestamp: new Date(Date.now() - 3600000).toISOString(), status: 'healthy', latency: 45, usage: 60 },
                { timestamp: new Date(Date.now() - 7200000).toISOString(), status: 'warning', latency: 75, usage: 80 },
                { timestamp: new Date(Date.now() - 10800000).toISOString(), status: 'healthy', latency: 50, usage: 65 },
                { timestamp: new Date(Date.now() - 14400000).toISOString(), status: 'healthy', latency: 40, usage: 55 },
            ]
        },
        {
            id: 2,
            name: 'Analysis Engine',
            status: 'warning',
            lastCheck: new Date().toISOString(),
            description: 'Processes and analyzes data',
            metrics: {
                latency: 85,
                usage: 75,
                errorRate: 8,
                throughput: 620,
            },
            errors: [
                { timestamp: new Date(Date.now() - 1800000).toISOString(), message: 'High latency detected' },
                { timestamp: new Date(Date.now() - 3600000).toISOString(), message: 'Usage above threshold' }
            ],
            history: [
                { timestamp: new Date(Date.now() - 3600000).toISOString(), status: 'warning', latency: 85, usage: 75 },
                { timestamp: new Date(Date.now() - 7200000).toISOString(), status: 'healthy', latency: 55, usage: 60 },
                { timestamp: new Date(Date.now() - 10800000).toISOString(), status: 'warning', latency: 80, usage: 70 },
                { timestamp: new Date(Date.now() - 14400000).toISOString(), status: 'healthy', latency: 50, usage: 55 },
            ]
        },
        {
            id: 3,
            name: 'Recommendation Engine',
            status: 'error',
            lastCheck: new Date().toISOString(),
            description: 'Generates AI-powered recommendations',
            metrics: {
                latency: 150,
                usage: 90,
                errorRate: 15,
                throughput: 320,
            },
            errors: [
                { timestamp: new Date(Date.now() - 900000).toISOString(), message: 'Service not responding' },
                { timestamp: new Date(Date.now() - 1800000).toISOString(), message: 'Connection timeout' }
            ],
            history: [
                { timestamp: new Date(Date.now() - 3600000).toISOString(), status: 'error', latency: 150, usage: 90 },
                { timestamp: new Date(Date.now() - 7200000).toISOString(), status: 'warning', latency: 95, usage: 85 },
                { timestamp: new Date(Date.now() - 10800000).toISOString(), status: 'error', latency: 140, usage: 95 },
                { timestamp: new Date(Date.now() - 14400000).toISOString(), status: 'warning', latency: 90, usage: 80 },
            ]
        }
    ]);

    const [expandedEngines, setExpandedEngines] = useState({});
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [selectedEngine, setSelectedEngine] = useState(null);
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [timelineDialogOpen, setTimelineDialogOpen] = useState(false);

    useEffect(() => {
        let interval;
        if (autoRefresh) {
            interval = setInterval(handleRefresh, 30000);
        }
        return () => clearInterval(interval);
    }, [autoRefresh]);

    const getStatusColor = (status) => {
        switch (status) {
            case 'healthy':
                return '#4CAF50';
            case 'warning':
                return '#FF9800';
            case 'error':
                return '#F44336';
            default:
                return '#757575';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'healthy':
                return <HealthyIcon sx={{ color: getStatusColor(status) }} />;
            case 'warning':
                return <WarningIcon sx={{ color: getStatusColor(status) }} />;
            case 'error':
                return <ErrorIcon sx={{ color: getStatusColor(status) }} />;
            default:
                return null;
        }
    };

    const handleRefresh = () => {
        const updatedEngines = engines.map(engine => {
            const newLatency = Math.floor(Math.random() * 200);
            const newUsage = Math.floor(Math.random() * 100);
            const newStatus = newLatency > 150 ? 'error' : newLatency > 80 ? 'warning' : 'healthy';
            
            return {
                ...engine,
                lastCheck: new Date().toISOString(),
                status: newStatus,
                metrics: {
                    ...engine.metrics,
                    latency: newLatency,
                    usage: newUsage,
                    errorRate: Math.floor(Math.random() * 20),
                    throughput: Math.floor(Math.random() * 1000),
                },
                history: [
                    { 
                        timestamp: new Date().toISOString(), 
                        status: newStatus,
                        latency: newLatency,
                        usage: newUsage
                    },
                    ...engine.history.slice(0, 10)
                ]
            };
        });
        setEngines(updatedEngines);
    };

    const handleRestartEngine = (engineId) => {
        const updatedEngines = engines.map(engine => 
            engine.id === engineId
                ? { 
                    ...engine, 
                    status: 'healthy', 
                    lastCheck: new Date().toISOString(),
                    errors: [],
                    metrics: {
                        ...engine.metrics,
                        latency: 45,
                        usage: 60,
                        errorRate: 0,
                        throughput: 800,
                    }
                }
                : engine
        );
        setEngines(updatedEngines);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const toggleEngineExpansion = (engineId) => {
        setExpandedEngines(prev => ({
            ...prev,
            [engineId]: !prev[engineId]
        }));
    };

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3
            }}>
                <MDTypography variant="h5" component="h2">
                    System Health Monitor
                </MDTypography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={autoRefresh}
                                onChange={(e) => setAutoRefresh(e.target.checked)}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: '#2B5587',
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: '#2B5587',
                                    },
                                }}
                            />
                        }
                        label="Auto Refresh"
                    />
                    <Tooltip title="Refresh all engine statuses">
                        <Button
                            variant="contained"
                            startIcon={<RefreshIcon />}
                            onClick={handleRefresh}
                        >
                            Refresh Status
                        </Button>
                    </Tooltip>
                </Box>
            </Box>
            <Grid container spacing={3}>
                {engines.map((engine) => (
                    <Grid item xs={12} md={4} key={engine.id}>
                        <Card 
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                position: 'relative',
                                '&:hover': {
                                    boxShadow: 3,
                                },
                            }}
                        >
                            <CardContent>
                                <Box sx={{ 
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    mb: 2
                                }}>
                                    <Box>
                                        <MDTypography 
                                            variant="h6" 
                                            component="h3"
                                            sx={{ mb: 1 }}
                                        >
                                            {engine.name}
                                        </MDTypography>
                                        <Chip
                                            icon={getStatusIcon(engine.status)}
                                            label={engine.status.toUpperCase()}
                                            size="small"
                                            sx={{
                                                backgroundColor: `${getStatusColor(engine.status)}15`,
                                                color: getStatusColor(engine.status),
                                                fontWeight: 500,
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Tooltip title="View performance history">
                                            <IconButton
                                                onClick={() => {
                                                    setSelectedEngine(engine);
                                                    setTimelineDialogOpen(true);
                                                }}
                                                sx={{
                                                    color: 'gold.main',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(43, 85, 135, 0.04)',
                                                    },
                                                }}
                                            >
                                                <TimelineIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="View status history">
                                            <IconButton
                                                onClick={() => {
                                                    setSelectedEngine(engine);
                                                    setHistoryDialogOpen(true);
                                                }}
                                                sx={{
                                                    color: 'gold.main',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(43, 85, 135, 0.04)',
                                                    },
                                                }}
                                            >
                                                <HistoryIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Restart engine">
                                            <IconButton
                                                onClick={() => handleRestartEngine(engine.id)}
                                                sx={{
                                                    color: 'gold.main',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(43, 85, 135, 0.04)',
                                                    },
                                                }}
                                            >
                                                <RestartIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>

                                <MDTypography 
                                    variant="body2" 
                                    sx={{ mb: 3 }}
                                >
                                    {engine.description}
                                </MDTypography>

                                {/* Gauges Section */}
                                <MDBox bgColor="navy.main" sx={{ mb: 3 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Box sx={{ 
                                                height: 130,
                                                position: 'relative',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'flex-start'
                                            }}>
                                                <Box sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    mb: 1.5
                                                }}>
                                                    <Typography 
                                                        variant="caption" 
                                                        sx={{ 
                                                            color: 'text.main',
                                                            mb: 0.5
                                                        }}
                                                    >
                                                        Latency
                                                    </Typography>
                                                    <Typography 
                                                        variant="h6" 
                                                        sx={{ 
                                                            color: 'text.main',
                                                            lineHeight: 1
                                                        }}
                                                    >
                                                        {engine.metrics.latency}ms
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ 
                                                    position: 'relative',
                                                    width: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                }}>
                                                    <Box sx={{
                                                        position: 'relative',
                                                        width: '100%',
                                                        display: 'flex',
                                                        justifyContent: 'center'
                                                    }}>
                                                        <PieChart
                                                            series={[
                                                                {
                                                                    data: [
                                                                        { id: 0, value: engine.metrics.latency, color: engine.metrics.latency > 150 ? '#F44336' : engine.metrics.latency > 80 ? '#FF9800' : '#4CAF50' },
                                                                        { id: 1, value: 200 - engine.metrics.latency, color: '#e0e0e0' }
                                                                    ],
                                                                    highlightScope: { faded: 'global', highlighted: 'item' },
                                                                    arcLabel: () => '',
                                                                    startAngle: -90,
                                                                    endAngle: 90,
                                                                    innerRadius: 0.6,
                                                                    paddingAngle: 0,
                                                                    cornerRadius: 4,
                                                                },
                                                            ]}
                                                            height={120}
                                                            width={100}
                                                            margin={{ left: 0, right: 0, top: 0, bottom: 0}}
                                                            slotProps={{
                                                                legend: { hidden: true }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{ 
                                                height: 130,
                                                position: 'relative',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'flex-start'
                                            }}>
                                                <Box sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    mb: 1.5
                                                }}>
                                                    <Typography 
                                                        variant="caption" 
                                                        sx={{ 
                                                            color: 'text.main',
                                                            mb: 0.5
                                                        }}
                                                    >
                                                        Usage
                                                    </Typography>
                                                    <Typography 
                                                        variant="h6" 
                                                        sx={{ 
                                                            color: 'text.main',
                                                            lineHeight: 1
                                                        }}
                                                    >
                                                        {engine.metrics.usage}%
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ 
                                                    position: 'relative',
                                                    width: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                }}>
                                                    <Box sx={{
                                                        position: 'relative',
                                                        width: '100%',
                                                        display: 'flex',
                                                        justifyContent: 'center'
                                                    }}>
                                                        <PieChart
                                                            series={[
                                                                {
                                                                    data: [
                                                                        { id: 0, value: engine.metrics.usage, color: engine.metrics.usage > 90 ? '#F44336' : engine.metrics.usage > 75 ? '#FF9800' : '#4CAF50' },
                                                                        { id: 1, value: 100 - engine.metrics.usage, color: '#e0e0e0' }
                                                                    ],
                                                                    highlightScope: { faded: 'global', highlighted: 'item' },
                                                                    arcLabel: () => '',
                                                                    startAngle: -90,
                                                                    endAngle: 90,
                                                                    innerRadius: 0.6,
                                                                    paddingAngle: 0,
                                                                    cornerRadius: 4,
                                                                },
                                                            ]}
                                                            height={120}
                                                            width={100}
                                                            margin={{ left: 0, right: 0, top: 0, bottom: 0}}
                                                            slotProps={{
                                                                legend: { hidden: true }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </MDBox>

                                {/* Additional Metrics */}
                                <Box sx={{ mb: 2 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Paper 
                                                elevation={0} 
                                                sx={{ 
                                                    p: 1, 
                                                    textAlign: 'center',
                                                    bgcolor: 'rgba(43, 85, 135, 0.04)'
                                                }}
                                            >
                                                <Typography variant="caption" color="text.main">
                                                    Error Rate
                                                </Typography>
                                                <Typography variant="h6" color="text.main">
                                                    {engine.metrics.errorRate}%
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Paper 
                                                elevation={0} 
                                                sx={{ 
                                                    p: 1, 
                                                    textAlign: 'center',
                                                    bgcolor: 'rgba(43, 85, 135, 0.04)'
                                                }}
                                            >
                                                <Typography variant="caption" color="text.main">
                                                    Throughput
                                                </Typography>
                                                <Typography variant="h6" color="text.main">
                                                    {engine.metrics.throughput}/s
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                </Box>

                                {/* Errors Section */}
                                {engine.errors.length > 0 && (
                                    <MDBox bgColor="navy.main" sx={{ mb: 2 }}>
                                        <Button
                                            size="small"
                                            onClick={() => toggleEngineExpansion(engine.id)}
                                            endIcon={expandedEngines[engine.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            sx={{ color: '#F44336' }}
                                        >
                                            {engine.errors.length} Error{engine.errors.length > 1 ? 's' : ''}
                                        </Button>
                                        <Collapse in={expandedEngines[engine.id]}>
                                            <List dense>
                                                {engine.errors.map((error, index) => (
                                                    <ListItem key={index}>
                                                        <ListItemText
                                                            primary={error.message}
                                                            secondary={formatDate(error.timestamp)}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Collapse>
                                    </MDBox>
                                )}

                                <Typography 
                                    variant="caption" 
                                    color="text.main"
                                    sx={{ 
                                        display: 'block',
                                        mt: 'auto'
                                    }}
                                >
                                    Last checked: {formatDate(engine.lastCheck)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* History Dialog */}
            <Dialog
                open={historyDialogOpen}
                onClose={() => setHistoryDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Status History - {selectedEngine?.name}
                </DialogTitle>
                <DialogContent>
                    <List>
                        {selectedEngine?.history.map((record, index) => (
                            <ListItem key={index}>
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {getStatusIcon(record.status)}
                                            <Typography sx={{ color: getStatusColor(record.status) }}>
                                                {record.status.toUpperCase()}
                                            </Typography>
                                        </Box>
                                    }
                                    secondary={formatDate(record.timestamp)}
                                />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Timeline Dialog */}
            <Dialog
                open={timelineDialogOpen}
                onClose={() => setTimelineDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Performance History - {selectedEngine?.name}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ height: 400, mt: 2 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                                data={selectedEngine?.history || []}
                                margin={{
                                    top: 5,
                                    right: 30,
                                    left: 20,
                                    bottom: 5,
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis 
                                    dataKey="timestamp" 
                                    tickFormatter={(timestamp) => {
                                        const date = new Date(timestamp);
                                        return `${date.getHours()}:${date.getMinutes()}`;
                                    }}
                                />
                                <YAxis />
                                <ChartTooltip />
                                <Line type="monotone" dataKey="latency" stroke="#2B5587" name="Latency (ms)" />
                                <Line type="monotone" dataKey="usage" stroke="#82ca9d" name="Usage (%)" />
                            </LineChart>
                        </ResponsiveContainer>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTimelineDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default SystemHealth; 